import React, { useEffect, useState } from "react";
import {
  listAssessments,
  createAssessment,
  deleteAssessment,
  uploadAssessmentQuestions,
  uploadAssessmentUsers,
  getAssessmentDetail,
  revokeAssessmentUser,
} from "../api/client";

/**
 * Admin → Assessments management.
 *
 * Three things admins do here:
 *   1. Create a new assessment (name + total questions / quotas).
 *   2. Upload a list of questions via Excel (same column schema as the
 *      Quiz3-Quiz6 fixture files).
 *   3. Upload a list of allowed users via Excel (single 'Email' column).
 *
 * The default "Initial Assessment" is auto-created on first server start
 * and holds every fixture-loaded question. It can't be deleted from this
 * page (the delete button is disabled for it).
 *
 * Styling uses the shared .aiq-* classes in index.css so themes work.
 */
export default function AdminAssessments() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null); // selected assessment full detail
  const [detailLoading, setDetailLoading] = useState(false);

  // Create form state
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newTotal, setNewTotal] = useState(45);
  const [newShort, setNewShort] = useState(20);
  const [newLong, setNewLong] = useState(25);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAssessments();
      setAssessments(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load assessments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createAssessment({
        name: newName.trim(),
        description: newDesc.trim() || null,
        total_questions: Number(newTotal) || 45,
        short_quota: Number(newShort) || 20,
        long_quota: Number(newLong) || 25,
      });
      setNewName("");
      setNewDesc("");
      await reload();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to create assessment");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (a) => {
    if (a.is_default) return;
    if (!window.confirm(`Delete assessment "${a.name}"? Linked questions stay; access grants are revoked.`)) return;
    try {
      await deleteAssessment(a.id);
      if (detail?.id === a.id) setDetail(null);
      await reload();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const handleUploadQuestions = async (a, file) => {
    if (!file) return;
    setBusyId(`q:${a.id}`);
    try {
      const res = await uploadAssessmentQuestions(a.id, file);
      const r = res.data;
      alert(
        `Done.\nAdded: ${r.added_questions}\nLinked: ${r.linked_questions}\nSkipped rows: ${r.skipped_rows}\nErrors: ${r.errors}`
      );
      await reload();
      if (detail?.id === a.id) await openDetail(a.id);
    } catch (err) {
      alert(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleUploadUsers = async (a, file) => {
    if (!file) return;
    setBusyId(`u:${a.id}`);
    try {
      const res = await uploadAssessmentUsers(a.id, file);
      const r = res.data;
      const unknownNote =
        r.unknown_count > 0
          ? `\n\nUnknown emails (${r.unknown_count}, first ${r.unknown_emails.length} shown):\n` +
            r.unknown_emails.join("\n")
          : "";
      alert(
        `Done.\nGranted: ${r.granted}\nAlready assigned: ${r.already_assigned}${unknownNote}`
      );
      await reload();
      if (detail?.id === a.id) await openDetail(a.id);
    } catch (err) {
      alert(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusyId(null);
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await getAssessmentDetail(id);
      setDetail(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to load detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRevokeUser = async (assessmentId, userId) => {
    if (!window.confirm("Revoke this user's assignment?")) return;
    try {
      await revokeAssessmentUser(assessmentId, userId);
      await openDetail(assessmentId);
      await reload();
    } catch (err) {
      alert(err?.response?.data?.detail || "Revoke failed");
    }
  };

  if (loading) return <div style={{ padding: 24 }} className="aiq-muted">Loading assessments…</div>;
  if (error) return <div style={{ padding: 24, color: "var(--color-incorrect)" }}>{error}</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <h1 style={{ marginTop: 0 }}>Assessments</h1>
      <p className="aiq-muted" style={{ marginTop: -4 }}>
        Manage assessments and who takes them. The default Initial Assessment
        holds every fixture-loaded question and is the fallback for users
        without an explicit assignment.
      </p>

      {/* Create form */}
      <form
        onSubmit={handleCreate}
        className="aiq-card"
        style={{ marginTop: 16, padding: 16 }}
      >
        <h3 style={{ marginTop: 0 }}>Create new assessment</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 3fr 1fr 1fr 1fr",
            gap: 12,
          }}
        >
          <div>
            <label className="aiq-label" htmlFor="ass-name">Name</label>
            <input
              id="ass-name"
              className="aiq-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Q4 2026 Senior Track"
              required
            />
          </div>
          <div>
            <label className="aiq-label" htmlFor="ass-desc">Description (optional)</label>
            <input
              id="ass-desc"
              className="aiq-input"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />
          </div>
          <div>
            <label className="aiq-label" htmlFor="ass-total">Total Q's</label>
            <input
              id="ass-total"
              className="aiq-input"
              type="number"
              min={1}
              value={newTotal}
              onChange={(e) => setNewTotal(e.target.value)}
            />
          </div>
          <div>
            <label className="aiq-label" htmlFor="ass-short">Short quota</label>
            <input
              id="ass-short"
              className="aiq-input"
              type="number"
              min={0}
              value={newShort}
              onChange={(e) => setNewShort(e.target.value)}
            />
          </div>
          <div>
            <label className="aiq-label" htmlFor="ass-long">Long quota</label>
            <input
              id="ass-long"
              className="aiq-input"
              type="number"
              min={0}
              value={newLong}
              onChange={(e) => setNewLong(e.target.value)}
            />
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <button
            type="submit"
            className="aiq-btn-primary"
            disabled={creating || !newName.trim()}
          >
            {creating ? "Creating…" : "Create assessment"}
          </button>
        </div>
      </form>

      {/* Assessment list */}
      <div className="aiq-card" style={{ marginTop: 16, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="aiq-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Questions</th>
                <th>Assigned</th>
                <th>Sessions</th>
                <th>Default</th>
                <th>Upload questions</th>
                <th>Upload users</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{a.name}</div>
                    {a.description && (
                      <div style={{ fontSize: 11 }} className="aiq-muted">
                        {a.description.slice(0, 80)}
                      </div>
                    )}
                  </td>
                  <td>{a.question_count}</td>
                  <td>{a.assigned_user_count}</td>
                  <td>{a.session_count}</td>
                  <td>
                    {a.is_default ? (
                      <span className="aiq-chip aiq-chip-active">Default</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <FilePicker
                      accept=".xlsx"
                      busy={busyId === `q:${a.id}`}
                      label="Excel"
                      onFile={(f) => handleUploadQuestions(a, f)}
                    />
                  </td>
                  <td>
                    <FilePicker
                      accept=".xlsx"
                      busy={busyId === `u:${a.id}`}
                      label="Excel"
                      onFile={(f) => handleUploadUsers(a, f)}
                    />
                  </td>
                  <td style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      className="aiq-btn-ghost"
                      onClick={() => openDetail(a.id)}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className="aiq-btn-danger"
                      disabled={a.is_default}
                      title={a.is_default ? "Default assessment can't be deleted" : "Delete"}
                      onClick={() => handleDelete(a)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {detail && (
        <div className="aiq-card" style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0 }}>{detail.name}</h2>
            <button
              type="button"
              className="aiq-btn-ghost"
              onClick={() => setDetail(null)}
            >
              Close
            </button>
          </div>
          {detailLoading ? (
            <div className="aiq-muted">Loading…</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 16 }}>
              <div>
                <h3 className="aiq-section-head">Questions ({detail.questions.length})</h3>
                <div style={{ maxHeight: 400, overflowY: "auto", marginTop: 8 }}>
                  <table className="aiq-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Module</th>
                        <th>Time</th>
                        <th>Text</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.questions.map((q) => (
                        <tr key={q.id}>
                          <td>{q.question_id_code || q.id}</td>
                          <td>{q.module || "—"}</td>
                          <td>{q.time_estimate_seconds ?? "—"}s</td>
                          <td style={{ fontSize: 12 }}>{q.question_text}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="aiq-section-head">Assigned users ({detail.assigned_users.length})</h3>
                <div style={{ maxHeight: 400, overflowY: "auto", marginTop: 8 }}>
                  <table className="aiq-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Granted</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.assigned_users.map((u) => (
                        <tr key={u.user_id}>
                          <td>{u.name || "—"}</td>
                          <td style={{ fontSize: 12 }}>{u.email}</td>
                          <td style={{ fontSize: 11 }} className="aiq-muted">
                            {u.granted_at ? u.granted_at.slice(0, 10) : ""}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="aiq-btn-danger"
                              onClick={() => handleRevokeUser(detail.id, u.user_id)}
                            >
                              Revoke
                            </button>
                          </td>
                        </tr>
                      ))}
                      {detail.assigned_users.length === 0 && (
                        <tr>
                          <td colSpan={4} className="aiq-muted">
                            Nobody assigned. Anyone takes the default assessment by fallback.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FilePicker = ({ accept, onFile, busy, label }) => (
  <label
    className="aiq-btn-ghost"
    style={{ cursor: busy ? "wait" : "pointer", display: "inline-block" }}
  >
    {busy ? "Uploading…" : label}
    <input
      type="file"
      accept={accept}
      style={{ display: "none" }}
      disabled={busy}
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) onFile(f);
        e.target.value = "";
      }}
    />
  </label>
);
